import React, { useState, useEffect, useMemo } from "react";
import {
    Button, Input, Modal, message, Skeleton, Empty,
    Select, Checkbox, Tooltip, Alert, Pagination,
} from "antd";
import { FaBuilding, FaSearch, FaPlus, FaTrash, FaWifi } from "react-icons/fa";
import ClinicFormModal from "./ClinicFormModal";
import ClinicCard from "./ClinicCard";
import PageTitle from "../../Ui/PageTitle";
import { useClinics } from "../../../hooks/useClinics";

const { Option } = Select;

const Clinics = () => {
    const [searchTerm, setSearchTerm]           = useState("");
    const [modalVisible, setModalVisible]       = useState(false);
    const [currentClinic, setCurrentClinic]     = useState(null);
    const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
    const [clinicToDelete, setClinicToDelete]   = useState(null);
    const [currentPage, setCurrentPage]         = useState(1);
    const [pageSize, setPageSize]               = useState(10);
    const [sortBy, setSortBy]                   = useState("name");
    const [sortOrder, setSortOrder]             = useState("asc");
    const [selectedClinics, setSelectedClinics] = useState([]);
    const [bulkDeleteVisible, setBulkDeleteVisible] = useState(false);
    const [isOnline, setIsOnline]               = useState(navigator.onLine);
    const [networkError, setNetworkError]       = useState(false);

    const {
        clinicsQuery,
        addClinic,
        updateClinic,
        deleteClinic,
        isDoctor,
    } = useClinics({ pageNumber: currentPage, pageSize });

    // ─── Client-side filter + sort (API only supports pagination) ──────────────
    const filteredAndSortedClinics = useMemo(() => {
        const raw = clinicsQuery.data?.clinics || [];

        const filtered = raw.filter((c) =>
            c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            c.detailedAddress.toLowerCase().includes(searchTerm.toLowerCase()) ||
            c.contactEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
            c.contactPhone.includes(searchTerm)
        );

        filtered.sort((a, b) => {
            let aVal, bVal;
            if (sortBy === "email") {
                aVal = a.contactEmail.toLowerCase();
                bVal = b.contactEmail.toLowerCase();
            } else if (sortBy === "date") {
                aVal = new Date(a.createdAt || 0);
                bVal = new Date(b.createdAt || 0);
            } else {
                aVal = a.name.toLowerCase();
                bVal = b.name.toLowerCase();
            }
            if (sortOrder === "asc") return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
            return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
        });

        return filtered;
    }, [clinicsQuery.data?.clinics, searchTerm, sortBy, sortOrder]);

    // Reset page + selection when search/sort changes
    useEffect(() => {
        setCurrentPage(1);
        setSelectedClinics([]);
    }, [searchTerm, sortBy, sortOrder]);

    // ─── Network monitoring ─────────────────────────────────────────────────────
    useEffect(() => {
        const handleOnline = () => {
            setIsOnline(true);
            setNetworkError(false);
            if (clinicsQuery.isError) clinicsQuery.refetch();
        };
        const handleOffline = () => { setIsOnline(false); setNetworkError(true); };
        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);
        return () => {
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
        };
    }, [clinicsQuery]);

    useEffect(() => {
        if (clinicsQuery.isError) {
            const err = clinicsQuery.error;
            if (!navigator.onLine || err?.code === "NETWORK_ERROR" ||
                err?.message?.includes("network") || err?.message?.includes("fetch")) {
                setNetworkError(true);
            }
        }
    }, [clinicsQuery.isError, clinicsQuery.error]);

    // ─── Keyboard shortcuts ─────────────────────────────────────────────────────
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
            if (e.key === "/") {
                e.preventDefault();
                document.querySelector('input[placeholder*="Search"]')?.focus();
            }
            if ((e.key === "n" || e.key === "N") && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                if (!isDoctor) showModal();
            }
            if (e.key === "Escape") setSelectedClinics([]);
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isDoctor]);

    // ─── Helpers ────────────────────────────────────────────────────────────────
    const isNetworkErr = (err) =>
        !navigator.onLine ||
        err?.code === "NETWORK_ERROR" ||
        err?.message?.includes("network") ||
        err?.message?.includes("fetch");

    const showModal = (clinic = null) => {
        setCurrentClinic(clinic);
        setModalVisible(true);
    };

    // ─── CRUD handlers ──────────────────────────────────────────────────────────
    const handleFinish = async (values) => {
        if (!navigator.onLine) {
            message.error("No internet connection. Please try again.");
            return;
        }
        try {
            if (currentClinic) {
                await updateClinic.mutateAsync({
                    id: currentClinic.id,
                    name: values.name,
                    nameAr: values.nameAr,
                    detailedAddress: values.detailedAddress,
                    detailedAddressAr: values.detailedAddressAr,
                    contactPhone: values.phone,
                    contactEmail: values.email,
                });
                message.success("Clinic updated successfully");
            } else {
                await addClinic.mutateAsync({
                    name: values.name,
                    nameAr: values.nameAr,
                    detailedAddress: values.detailedAddress,
                    detailedAddressAr: values.detailedAddressAr,
                    contactPhone: values.phone,
                    contactEmail: values.email,
                });
                message.success("Clinic added successfully");
            }
            setModalVisible(false);
            setNetworkError(false);
        } catch (error) {
            if (isNetworkErr(error)) {
                setNetworkError(true);
                message.error("Network error. Please check your connection.");
            } else {
                message.error(error.message || "Something went wrong.");
            }
        }
    };

    const handleDelete = (id) => {
        setClinicToDelete(id);
        setDeleteConfirmVisible(true);
    };

    const confirmDelete = async () => {
        if (!navigator.onLine) {
            message.error("No internet connection. Please try again.");
            return;
        }
        try {
            await deleteClinic.mutateAsync(clinicToDelete);
            message.success("Clinic deleted successfully");
            setDeleteConfirmVisible(false);
            setNetworkError(false);
        } catch (error) {
            if (isNetworkErr(error)) {
                setNetworkError(true);
                message.error("Network error. Please check your connection.");
            } else {
                message.error(error.message || "Failed to delete clinic.");
            }
        }
    };

    const confirmBulkDelete = async () => {
        if (!navigator.onLine) {
            message.error("No internet connection. Please try again.");
            return;
        }
        try {
            await Promise.all(selectedClinics.map((id) => deleteClinic.mutateAsync(id)));
            message.success(`${selectedClinics.length} clinics deleted successfully`);
            setSelectedClinics([]);
            setBulkDeleteVisible(false);
            setNetworkError(false);
        } catch (error) {
            if (isNetworkErr(error)) {
                setNetworkError(true);
                message.error("Network error. Please check your connection.");
            } else {
                message.error("Failed to delete some clinics.");
            }
        }
    };

    // ─── Bulk selection ─────────────────────────────────────────────────────────
    const ownedClinics = filteredAndSortedClinics.filter((c) => c.isOwned);

    const handleSelectAll = (checked) => {
        setSelectedClinics(checked ? ownedClinics.map((c) => c.id) : []);
    };

    const handleSelectClinic = (id, checked) => {
        setSelectedClinics((prev) => checked ? [...prev, id] : prev.filter((i) => i !== id));
    };

    // ─── Pagination ─────────────────────────────────────────────────────────────
    const totalClinics   = clinicsQuery.data?.totalCount || 0;
    const allOwnedSelected =
        ownedClinics.length > 0 && ownedClinics.every((c) => selectedClinics.includes(c.id));

    const handlePageChange = (page, size) => {
        setCurrentPage(page);
        if (size !== pageSize) setPageSize(size);
        setSelectedClinics([]);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // ─── Render ─────────────────────────────────────────────────────────────────
    return (
        <div className="animate-fade-in">

            {/* Header */}
            <div className="flex flex-col md:flex-row mb-3 items-center justify-between">
                <PageTitle
                    actionProps={{
                        disabled: isDoctor || !isOnline || networkError || addClinic.isLoading,
                        loading: addClinic.isLoading,
                    }}
                />
            </div>

            {/* Search + Sort Bar */}
            <div className="mb-6 bg-white p-4 rounded-lg shadow-sm border">
                <div className="flex flex-col lg:flex-row gap-4">

                    {/* Search */}
                    <div className="flex-1">
                        <Input
                            placeholder="Search by name, address, email or phone… (Press '/' to focus)"
                            prefix={<FaSearch className="text-gray-400" />}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            allowClear
                            onClear={() => setSearchTerm("")}
                            size="large"
                        />
                    </div>

                    {/* Sort */}
                    <div className="flex gap-2">
                        <Tooltip title="Sort by">
                            <Select value={sortBy} onChange={setSortBy} style={{ width: 140 }} size="large">
                                <Option value="name">Name</Option>
                                <Option value="email">Email</Option>
                                <Option value="date">Date Added</Option>
                            </Select>
                        </Tooltip>

                        <Tooltip title="Order">
                            <Select value={sortOrder} onChange={setSortOrder} style={{ width: 120 }} size="large">
                                <Option value="asc">A-Z ↑</Option>
                                <Option value="desc">Z-A ↓</Option>
                            </Select>
                        </Tooltip>

                        <Tooltip title="Items per page">
                            <Select
                                value={pageSize}
                                onChange={(size) => { setPageSize(size); setCurrentPage(1); }}
                                style={{ width: 130 }}
                                size="large"
                            >
                                <Option value={5}>5 per page</Option>
                                <Option value={10}>10 per page</Option>
                                <Option value={20}>20 per page</Option>
                                <Option value={50}>50 per page</Option>
                            </Select>
                        </Tooltip>
                    </div>
                </div>

                {/* Bulk actions bar */}
                {!isDoctor && selectedClinics.length > 0 && (
                    <div className="mt-3 flex items-center gap-3 p-2 bg-blue-50 rounded-lg border border-blue-200">
                        <span className="text-sm text-blue-700 font-medium">
                            {selectedClinics.length} clinic{selectedClinics.length > 1 ? "s" : ""} selected
                        </span>
                        <Button
                            danger size="small" icon={<FaTrash />}
                            onClick={() => setBulkDeleteVisible(true)}
                            disabled={!isOnline || networkError}
                        >
                            Delete Selected
                        </Button>
                        <Button size="small" onClick={() => setSelectedClinics([])}>
                            Clear
                        </Button>
                    </div>
                )}
            </div>

            {/* Results */}
            {clinicsQuery.isLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[...Array(pageSize)].map((_, i) => (
                        <div key={i} className="bg-white rounded-lg border p-4">
                            <Skeleton active paragraph={{ rows: 4 }} />
                        </div>
                    ))}
                </div>

            ) : clinicsQuery.isError ? (
                <div className="text-center py-12 bg-white rounded-lg border">
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                            <div>
                                <span className="text-red-500 block mb-2">Failed to load clinics</span>
                                <span className="text-gray-500 text-sm">
                                    {clinicsQuery.error?.message || "Please check your connection and try again"}
                                </span>
                            </div>
                        }
                    >
                        <Button type="primary" onClick={() => clinicsQuery.refetch()} loading={clinicsQuery.isFetching}>
                            Try Again
                        </Button>
                    </Empty>
                </div>

            ) : totalClinics === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border">
                    <FaBuilding size={48} className="mx-auto mb-4 text-gray-400" />
                    <h3 className="text-lg font-medium mb-2">No clinics added yet</h3>
                    <p className="text-gray-500 mb-4">Start by adding your first clinic</p>
                    {!isDoctor && (
                        <Button
                            type="primary" icon={<FaPlus />} size="large"
                            onClick={() => showModal()}
                            disabled={!isOnline || networkError}
                        >
                            Add Your First Clinic
                        </Button>
                    )}
                </div>

            ) : filteredAndSortedClinics.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border">
                    <FaSearch size={48} className="mx-auto mb-4 text-gray-400" />
                    <h3 className="text-lg font-medium mb-2">No matching clinics found</h3>
                    <p className="text-gray-500 mb-4">
                        No results for &ldquo;{searchTerm}&rdquo;. Try a different term.
                    </p>
                    <Button onClick={() => setSearchTerm("")}>Clear Search</Button>
                </div>

            ) : (
                <>
                    {/* Select-all row */}
                    {!isDoctor && ownedClinics.length > 1 && (
                        <div className="mb-3 flex items-center gap-2">
                            <Checkbox
                                checked={allOwnedSelected}
                                indeterminate={selectedClinics.length > 0 && !allOwnedSelected}
                                onChange={(e) => handleSelectAll(e.target.checked)}
                            >
                                Select all on this page
                            </Checkbox>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredAndSortedClinics.map((clinic) => (
                            <div key={clinic.id} className="relative">
                                {!isDoctor && clinic.isOwned && ownedClinics.length > 1 && (
                                    <Checkbox
                                        className="absolute top-2 left-2 z-10 bg-white rounded shadow-sm"
                                        checked={selectedClinics.includes(clinic.id)}
                                        onChange={(e) => handleSelectClinic(clinic.id, e.target.checked)}
                                    />
                                )}
                                <ClinicCard
                                    clinic={{
                                        ...clinic,
                                        address: clinic.detailedAddress,
                                        phone: clinic.contactPhone,
                                        email: clinic.contactEmail,
                                    }}
                                    onEdit={!isDoctor && clinic.isOwned ? () => showModal(clinic) : null}
                                    onDelete={!isDoctor && clinic.isOwned ? () => handleDelete(clinic.id) : null}
                                    loading={deleteClinic.isLoading && deleteClinic.variables === clinic.id}
                                    selected={selectedClinics.includes(clinic.id)}
                                    disabled={isDoctor || !isOnline || networkError}
                                    readOnly={isDoctor}
                                />
                            </div>
                        ))}

                        {/* Optimistic skeleton while adding */}
                        {addClinic.isLoading && (
                            <div className="border border-gray-200 rounded-lg p-4 bg-white">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="w-4 h-4 bg-blue-200 rounded animate-pulse" />
                                    <span className="text-sm text-blue-600">Creating clinic…</span>
                                </div>
                                <Skeleton active paragraph={{ rows: 3 }} />
                            </div>
                        )}
                    </div>

                    {/* Pagination */}
                    {totalClinics > pageSize && (
                        <div className="mt-8 flex justify-center">
                            <Pagination
                                current={currentPage}
                                total={totalClinics}
                                pageSize={pageSize}
                                onChange={handlePageChange}
                                onShowSizeChange={handlePageChange}
                                showSizeChanger
                                showQuickJumper
                                showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} clinics`}
                                pageSizeOptions={["5", "10", "20", "50"]}
                                className="bg-white p-4 rounded-lg shadow-sm"
                                disabled={clinicsQuery.isLoading || !isOnline || networkError}
                            />
                        </div>
                    )}
                </>
            )}

            {/* Network alert */}
            {(networkError || !isOnline) && (
                <div className="fixed bottom-4 right-4 z-50">
                    <Alert
                        message={
                            <div className="flex items-center gap-2">
                                <FaWifi className="text-red-500" />
                                <span>{!isOnline ? "No internet connection" : "Network error"}</span>
                            </div>
                        }
                        description="Some features may be unavailable. Please check your connection."
                        type="warning"
                        showIcon
                        closable
                        onClose={() => setNetworkError(false)}
                    />
                </div>
            )}

            {/* Add / Edit modal — hidden for doctors */}
            {!isDoctor && (
                <ClinicFormModal
                    visible={modalVisible}
                    onCancel={() => setModalVisible(false)}
                    onFinish={handleFinish}
                    initialValues={
                        currentClinic
                            ? {
                                name: currentClinic.name,
                                nameAr: currentClinic.nameAr,
                                email: currentClinic.contactEmail,
                                phone: currentClinic.contactPhone,
                                detailedAddress: currentClinic.detailedAddress,
                                detailedAddressAr: currentClinic.detailedAddressAr,
                            }
                            : null
                    }
                    loading={addClinic.isLoading || updateClinic.isLoading}
                    existingClinics={clinicsQuery.data?.clinics || []}
                />
            )}

            {/* Delete confirmation */}
            <Modal
                title="Confirm Delete"
                open={deleteConfirmVisible}
                onOk={confirmDelete}
                onCancel={() => setDeleteConfirmVisible(false)}
                okText="Delete"
                okButtonProps={{ danger: true, loading: deleteClinic.isLoading }}
                cancelText="Cancel"
            >
                <p>Are you sure you want to delete this clinic? This action cannot be undone.</p>
            </Modal>

            {/* Bulk delete confirmation */}
            <Modal
                title={`Delete ${selectedClinics.length} Clinics`}
                open={bulkDeleteVisible}
                onOk={confirmBulkDelete}
                onCancel={() => setBulkDeleteVisible(false)}
                okText={`Delete ${selectedClinics.length} Clinics`}
                okButtonProps={{ danger: true, loading: deleteClinic.isLoading }}
                cancelText="Cancel"
            >
                <p>
                    Are you sure you want to delete <strong>{selectedClinics.length}</strong> selected clinics?
                    This action cannot be undone.
                </p>
            </Modal>
        </div>
    );
};

export default Clinics;